import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useDismissOrderCancellationBanner} from '../../../../common/react-query/hooks/mutations/useDismissOrderCancellationBanner'
import {useGetDismissedOrderCancellationBanners} from '../../../../common/react-query/hooks/queries/useGetDismissedOrderCancellationBanners'
import {hubQueryClient, useHubQuery} from '../../../../common/react-query/QueryProvider'
import {determineCancellationBannerState} from '../utils/determineCancellationBannerState'

import {useCancellationPropagationDelay} from './useCancellationPropagationDelay'

const POLLING_INTERVAL_MS = 10000
const PROPAGATION_DELAY_MS = 5000
const REFRESH_OVERLAY_MIN_DURATION_MS = 3000

interface UseOrderCancellationBannerOptions {
  orderId: string
  orderStatus?: string
  enabled?: boolean
  onOrderDataChanged?: () => void
}

export const useOrderCancellationBanner = ({
  orderId,
  orderStatus,
  enabled = true,
  onOrderDataChanged
}: UseOrderCancellationBannerOptions) => {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setIsRefreshing(false)
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
  }, [orderId])

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [])

  const dismissedBannersQueryInfo = useGetDismissedOrderCancellationBanners(enabled)
  const dismissedRequestIds = dismissedBannersQueryInfo.data ?? []

  const initialRequestQueryInfo = useHubQuery('cancellationRequestStatus', [orderId], {
    enabled: !!orderId && enabled,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false
  })

  const initialBannerState = useMemo(
    () =>
      determineCancellationBannerState(
        initialRequestQueryInfo.data,
        orderStatus,
        dismissedRequestIds
      ),
    [dismissedRequestIds, initialRequestQueryInfo.data, orderStatus]
  )
  const shouldPoll = initialBannerState.variant === 'cancellationProcessing'

  const pollingRequestQueryInfo = useHubQuery('cancellationRequestStatus', [orderId], {
    enabled: !!orderId && enabled && shouldPoll,
    refetchInterval: shouldPoll ? POLLING_INTERVAL_MS : false,
    retry: false
  })

  const latestRequest = shouldPoll ? pollingRequestQueryInfo.data : initialRequestQueryInfo.data
  const bannerState = useMemo(
    () => determineCancellationBannerState(latestRequest, orderStatus, dismissedRequestIds),
    [dismissedRequestIds, latestRequest, orderStatus]
  )
  const isPropagating = useCancellationPropagationDelay(
    latestRequest,
    PROPAGATION_DELAY_MS,
    orderId
  )
  const dismissBannerMutation = useDismissOrderCancellationBanner()

  const onRefresh = useCallback(() => {
    if (isRefreshing) return

    setIsRefreshing(true)
    void hubQueryClient.invalidateQueries(['order', orderId])
    void hubQueryClient.invalidateQueries('orders')
    void hubQueryClient.invalidateQueries(['cancellationRequestStatus', orderId])
    onOrderDataChanged?.()

    refreshTimerRef.current = setTimeout(() => {
      setIsRefreshing(false)
      refreshTimerRef.current = null
    }, REFRESH_OVERLAY_MIN_DURATION_MS)
  }, [isRefreshing, onOrderDataChanged, orderId])

  const onDismiss = useCallback(() => {
    if (latestRequest?.requestId) {
      dismissBannerMutation.mutate({requestId: latestRequest.requestId})
    }
  }, [dismissBannerMutation, latestRequest?.requestId])

  const showProcessing = isPropagating
  const isStatusUnavailable =
    initialRequestQueryInfo.isLoading ||
    initialRequestQueryInfo.isError ||
    pollingRequestQueryInfo.isError

  return {
    showBanner: showProcessing || bannerState.showBanner,
    variant: showProcessing ? ('cancellationProcessing' as const) : bannerState.variant,
    hasProcessingRequest:
      bannerState.variant === 'cancellationProcessing' ||
      isPropagating ||
      isRefreshing ||
      isStatusUnavailable,
    onRefresh,
    onDismiss,
    request: latestRequest,
    isRefreshing,
    isDismissing: dismissBannerMutation.isLoading,
    isLoading: initialRequestQueryInfo.isLoading || pollingRequestQueryInfo.isFetching,
    isError: initialRequestQueryInfo.isError || pollingRequestQueryInfo.isError
  }
}
